import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DateTimeFormatter } from '../../../formatters';
import { FlagOutline, FlagSolid, HomeOutline, PhoneOutline, RefreshOutline, SupportOutline, VisibilityOutline } from '../../../icons/icons';
import { SystemHubService } from '../../../services/axios/axios-api-v2-generated';
import { SystemHubImportanceLevel } from '../../../types/systemhub-notification';
import { LoadingIndicatorLinear } from '../../components/indicators/LoadingIndicatorLinear';
import CustomPopover from '../../components/layout/CustomPopover';
import './system-hub.css';

const KEY = ['system-hub'];
export function SystemHub() {
    const { data, status, isLoading, isRefetching, refetch, dataUpdatedAt } = useQuery({
        queryKey: KEY,
        queryFn: () => SystemHubService.getDataAsync({ withAuthorization: true })
    });

    return (
        <div className="h-100 overflow-auto">
            <div className="px-3">
                <div style={{ position: 'sticky', top: 0 }} className="pb-2 bg-color z-1">
                    {(isLoading || isRefetching) && <LoadingIndicatorLinear />}
                    <div className="d-flex gap-2 align-items-center pt-2">
                        <SvgIcon icon={FlagOutline} size="large" />
                        <div style={{ fontSize: '1.2rem', fontWeight: 500 }}>System hub</div>
                        {status === 'success' && (
                            <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                                <div className="secondary-text">Last update: {DateTimeFormatter.formatTime(dataUpdatedAt / 1000)}</div>
                                <Button title="Refresh" fillMode="flat" svgIcon={RefreshOutline} onClick={() => refetch()}></Button>
                            </div>
                        )}
                    </div>
                </div>
                {data && (
                    <div className="d-flex flex-column flex-lg-row justify-content-center gap-lg-3">
                        <div className="flex-grow-1">
                            <Box title="Notices / Alerts">
                                <table className="cobalt-table cobalt-alternate">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Date/Time</th>
                                            <th>Type</th>
                                            <th>Title</th>
                                            <th>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.noticeAlerts?.map((x) => (
                                            <tr key={x.id}>
                                                <td className="ps-2">
                                                    <SvgIcon
                                                        icon={FlagSolid}
                                                        className={importanceColors[x.importanceLevel ?? SystemHubImportanceLevel.none]}
                                                    />
                                                </td>
                                                <td>{DateTimeFormatter.formatDateTime(dayjs(x.time).unix())}</td>
                                                <td>{x.type === 0 ? 'Notice' : 'Alert'}</td>
                                                <td>{x.title}</td>
                                                <td>
                                                    <CustomPopover
                                                        placement="bottom"
                                                        trigger={<Button svgIcon={VisibilityOutline} fillMode="flat"></Button>}>
                                                        <div className="k-card p-2">
                                                            <strong>{x.title}</strong>
                                                            <div>{x.description}</div>
                                                        </div>
                                                    </CustomPopover>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                            <Box title="Scheduled Maintenance">
                                <table className="cobalt-table cobalt-alternate">
                                    <thead>
                                        <tr>
                                            <th className="ps-2">Start</th>
                                            <th>End</th>
                                            <th>Type</th>
                                            <th>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.scheduledMaintenances?.map((x) => (
                                            <tr key={x.id}>
                                                <td className="ps-2">{DateTimeFormatter.formatDateTime(dayjs(x.startTime).unix())}</td>
                                                <td>{DateTimeFormatter.formatDateTime(dayjs(x.endTime).unix())}</td>
                                                <td>{x.title}</td>
                                                <td>
                                                    <CustomPopover
                                                        placement="bottom"
                                                        trigger={<Button svgIcon={VisibilityOutline} fillMode="flat"></Button>}>
                                                        <div className="k-card p-2">
                                                            <strong>{x.title}</strong>
                                                            <div>{x.description}</div>
                                                        </div>
                                                    </CustomPopover>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                            <Box title="System / Feed Status">
                                <table className="cobalt-table cobalt-alternate">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Type</th>
                                            <th>Description</th>
                                            <th>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.feeds?.map((x) => (
                                            <tr key={x.id}>
                                                <td className="d-flex justify-content-center">
                                                    <div
                                                        className="system-hub-square"
                                                        style={{
                                                            backgroundColor:
                                                                x.color && x.color in feedStatusColors
                                                                    ? feedStatusColors[x.color]
                                                                    : undefined
                                                        }}></div>
                                                </td>
                                                <td>{x.type}</td>
                                                <td>{x.name}</td>
                                                <td>
                                                    <span>{x.mode}</span>
                                                    {x.mode && x.state && <span>/</span>}
                                                    <span>{x.state}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </div>
                        <div className="flex-grow-1">
                            <Box title="News & Announcements">
                                <div className="d-flex flex-column gap-2">
                                    {data?.newsAnnouncements?.map((x) => (
                                        <div key={x.id} className="d-flex gap-2 py-2">
                                            <div
                                                style={{
                                                    flexShrink: 0,
                                                    width: 6,
                                                    borderRadius: 4,
                                                    backgroundColor:
                                                        importanceColors[x.importanceLevel ?? SystemHubImportanceLevel.none] ||
                                                        'light-dark(var(--color-neutral-600), var(--color-neutral-400))'
                                                }}></div>
                                            <div>
                                                <div>{DateTimeFormatter.formatDateTime(dayjs(x.time).unix())}</div>
                                                <div style={{ fontWeight: 500 }}>{x.title}</div>
                                                <div>{x.description}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Box>
                            <Box title="Training / Features">
                                <div className="d-flex flex-column gap-2">
                                    {data?.trainingFeatures?.map((x) => (
                                        <div key={x.id} className="d-flex gap-2 py-2">
                                            <div
                                                style={{
                                                    flexShrink: 0,
                                                    width: 6,
                                                    borderRadius: 4,
                                                    backgroundColor:
                                                        importanceColors[x.importanceLevel ?? SystemHubImportanceLevel.none] ||
                                                        'light-dark(var(--color-neutral-600), var(--color-neutral-400))'
                                                }}></div>
                                            <div>
                                                <div>{DateTimeFormatter.formatDateTime(dayjs(x.time).unix())}</div>
                                                <div style={{ fontWeight: 500 }}>{x.title}</div>
                                                <div>{x.description}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Box>
                            <Box title="Support">
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'max-content 1fr',
                                        columnGap: '0.5rem',
                                        rowGap: '0.25rem',
                                        alignItems: 'center'
                                    }}>
                                    <SvgIcon icon={HomeOutline} />
                                    <a href="https://www.flightexplorer.com/" target="_blank" rel="noopener noreferrer">
                                        Flight Explorer
                                    </a>
                                    <SvgIcon icon={SupportOutline} />
                                    <a href="https://communityportal.flightservices.cae.com/" target="_blank">
                                        CAE Self Service Portal
                                    </a>

                                    <SvgIcon icon={PhoneOutline} />
                                    <a href="tel:877-347-0026">US: 877-347-0026</a>

                                    <SvgIcon icon={PhoneOutline} />
                                    <a href="tel:877-381-0929">Canada: 877-381-0929</a>

                                    <SvgIcon icon={PhoneOutline} />
                                    <a href="tel:+1218-229-6401">Worldwide: +1 218-229-6401</a>
                                </div>
                            </Box>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
function Box(props: { title: string; children?: React.ReactNode }) {
    return (
        <div className="k-card p-2 mb-3">
            <div style={{ fontSize: '1.2em', fontWeight: 500 }}>{props.title}</div>
            {props.children}
        </div>
    );
}

const importanceColors: Record<SystemHubImportanceLevel, string> = {
    [SystemHubImportanceLevel.none]: '',
    [SystemHubImportanceLevel.green]: 'text-success',
    [SystemHubImportanceLevel.yellow]: 'text-warning',
    [SystemHubImportanceLevel.red]: 'text-error'
};

const feedStatusColors: Partial<Record<number, string>> = {
    1: 'var(--color-red-400)',
    2: 'var(--color-yellow-400)',
    3: 'var(--color-green-400)'
};
